import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardBody, Image, Input, Chip, Spinner } from '@nextui-org/react';
import { Search, MapPin, Phone, Mail, Trophy, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import appDataService, { Series } from '../services/appDataService';

const SeriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState<Series[]>([]);
  const [filteredSeries, setFilteredSeries] = useState<Series[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSeries();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSeries(series);
    } else {
      const filtered = series.filter(seriesItem =>
        seriesItem.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        seriesItem.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        seriesItem.address?.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        seriesItem.categories?.some(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredSeries(filtered);
    }
  }, [searchTerm, series]);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const seriesData = await appDataService.getSeries();
      setSeries(seriesData);
      setFilteredSeries(seriesData);
    } catch (err) {
      console.error('Fehler beim Laden der Turnierserien:', err);
      setError('Turnierserien konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Turnierserien - DartCloud</title>
          <meta name="description" content="Turnierserien - Entdecke regelmäßige Dart-Turnierserien in deiner Nähe" />
        </Helmet>
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" color="primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-20">
        <Helmet>
          <title>Turnierserien - DartCloud</title>
          <meta name="description" content="Turnierserien - Entdecke regelmäßige Dart-Turnierserien in deiner Nähe" />
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center text-gray-500">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-20">
      <Helmet>
        <title>Turnierserien - DartCloud</title>
        <meta name="description" content="Turnierserien - Entdecke regelmäßige Dart-Turnierserien in deiner Nähe" />
        <meta name="keywords" content="Turnierserie, Dart Liga, Dart Serie, regelmäßige Turniere, Dart Events" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Trophy className="text-yellow-500" size={40} />
            Turnierserien
          </h1>
          <p className="text-gray-400">
            Entdecke regelmäßige Dart-Turnierserien und Events
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {filteredSeries.length} von {series.length} Turnierserien
          </p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder="Turnierserien durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startContent={<Search size={20} className="text-gray-400" />}
            className="max-w-md"
            classNames={{
              input: "bg-zinc-900",
              inputWrapper: "bg-zinc-900 border-zinc-800"
            }}
          />
        </div>

        {/* Series Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSeries.map((seriesItem) => (
            <Card 
              key={seriesItem._id}
              className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 transition-colors cursor-pointer"
              isPressable
              onPress={() => seriesItem.slug && navigate(`/turnierserie/${seriesItem.slug}`)}
            >
              <CardBody className="p-0">
                {/* Image */}
                <div className="relative">
                  <Image
                    removeWrapper
                    alt={appDataService.formatName(seriesItem)}
                    className="w-full h-48 object-cover"
                    src={appDataService.getImageUrl(seriesItem) || '/icon.png'}
                    fallbackSrc="/icon.png"
                  />
                  {seriesItem.verified && (
                    <div className="absolute top-3 right-3">
                      <Chip 
                        size="sm" 
                        className="bg-yellow-500 text-black"
                        variant="solid"
                      >
                        Verifiziert
                      </Chip>
                    </div>
                  )}
                  {seriesItem.tournaments && seriesItem.tournaments.length > 0 && (
                    <div className="absolute top-3 left-3">
                      <Chip 
                        size="sm" 
                        className="bg-zinc-800/80 text-white"
                        variant="solid"
                        startContent={<Calendar size={12} />}
                      >
                        {seriesItem.tournaments.length} Turniere
                      </Chip>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-2 line-clamp-1">
                    {appDataService.formatName(seriesItem)}
                  </h3>
                  
                  {seriesItem.description && (
                    <p className="text-sm text-gray-400 line-clamp-3 mb-3">
                      {seriesItem.description}
                    </p>
                  )}

                  {/* Categories */}
                  {seriesItem.categories && seriesItem.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {seriesItem.categories.slice(0, 3).map((category, index) => (
                        <Chip
                          key={index}
                          size="sm"
                          variant="flat"
                          className="bg-yellow-500/20 text-yellow-300"
                        >
                          {category}
                        </Chip>
                      ))}
                      {seriesItem.categories.length > 3 && (
                        <Chip
                          size="sm"
                          variant="flat"
                          className="bg-zinc-800 text-gray-400"
                        >
                          +{seriesItem.categories.length - 3}
                        </Chip>
                      )}
                    </div>
                  )}

                  {/* Tags */}
                  {seriesItem.tags && seriesItem.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {seriesItem.tags.slice(0, 2).map((tag, index) => (
                        <Chip
                          key={index}
                          size="sm"
                          variant="bordered"
                          className="border-zinc-700 text-gray-400"
                        >
                          #{tag}
                        </Chip>
                      ))}
                      {seriesItem.tags.length > 2 && (
                        <Chip
                          size="sm"
                          variant="bordered"
                          className="border-zinc-700 text-gray-400"
                        >
                          +{seriesItem.tags.length - 2}
                        </Chip>
                      )}
                    </div>
                  )}

                  {/* Address */}
                  {seriesItem.address && (
                    <div className="flex items-start gap-2 text-sm text-gray-400 mb-2">
                      <MapPin size={14} className="mt-1 flex-shrink-0" />
                      <span className="line-clamp-2">
                        {appDataService.formatAddress(seriesItem.address)}
                      </span>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="space-y-1">
                    {seriesItem.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Phone size={12} />
                        <span className="line-clamp-1">{seriesItem.phone}</span>
                      </div>
                    )}
                    
                    {seriesItem.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Mail size={12} />
                        <span className="line-clamp-1">{seriesItem.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredSeries.length === 0 && !loading && (
          <div className="text-center py-12">
            <Trophy size={64} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              Keine Turnierserien gefunden
            </h3>
            <p className="text-gray-500">
              {searchTerm ? 'Versuche einen anderen Suchbegriff' : 'Aktuell sind keine Turnierserien verfügbar'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeriesPage;